// Per-table column layout: which columns are shown, in what order, and how wide.
// Kept in localStorage rather than on the user record so it costs no round trip,
// and kept out of redux so there is no reactor to loop.

const STORAGE_PREFIX = 'archTableLayout:';
const VERSION = 1;

// Narrower than this and a column holds a couple of characters and an ellipsis,
// which is no use to anyone; wider than the maximum and it pushes everything else
// off the side. A column whose contents need more room says so with its own
// `minWidth`.
export const MIN_COLUMN_WIDTH = 100;
export const MAX_COLUMN_WIDTH = 800;

const storageKey = tableId => `${STORAGE_PREFIX}${tableId}`;

// `hidden` lists what to leave out rather than what to show, so a column added
// after a layout was saved turns up for everyone instead of silently missing.
export const defaultLayout = (definitions = []) => ({
  v: VERSION,
  order: [],
  hidden: definitions.filter(d => d.defaultHidden).map(d => d.id),
  widths: {},
});

const ids = value =>
  Array.isArray(value) ? value.filter(id => typeof id === 'string') : [];

// Anything we did not write ourselves — a hand-edited entry, a layout from an
// older shape — is treated as absent so the table falls back to its defaults.
const sanitise = raw => {
  if (!raw || typeof raw !== 'object' || raw.v !== VERSION) return null;
  return {
    v: VERSION,
    order: ids(raw.order),
    hidden: ids(raw.hidden),
    widths:
      raw.widths && typeof raw.widths === 'object' ? { ...raw.widths } : {},
  };
};

// null when there is nothing usable stored, which is what lets `defaultHidden`
// apply on a first visit but not once the user has had their say.
export function readLayout(tableId) {
  try {
    return sanitise(JSON.parse(localStorage.getItem(storageKey(tableId))));
  } catch (error) {
    // Storage unavailable (private mode, blocked): the table uses its defaults.
    return null;
  }
}

export function writeLayout(tableId, layout) {
  try {
    localStorage.setItem(storageKey(tableId), JSON.stringify(layout));
  } catch (error) {
    // Storage unavailable: the layout lasts as long as the page does.
  }
}

const clamp = (definition, px) =>
  Math.max(
    definition.minWidth || MIN_COLUMN_WIDTH,
    Math.min(MAX_COLUMN_WIDTH, Math.round(px)),
  );

// react-virtualized needs a width on the Column itself, so that is where a
// definition's default width lives rather than being repeated alongside it.
const columnProp = (definition, name) => {
  if (definition[name] !== undefined) return definition[name];
  const { element } = definition;
  return element && element.props ? element.props[name] : undefined;
};

const widthFor = (definition, widths) => {
  const declared = columnProp(definition, 'width');
  if (definition.pinned) return declared;
  const stored = Number(widths[definition.id]);
  if (!Number.isFinite(stored) || stored <= 0) return declared;
  return clamp(definition, stored);
};

// Definition order for the pinned columns, saved order for the rest. Columns the
// saved order has never seen go on the end; ids it knows that no longer exist
// are dropped.
const orderedIds = (definitions, order) => {
  const pinnedTo = place =>
    definitions.filter(d => d.pinned === place).map(d => d.id);
  const start = pinnedTo('start');
  const end = pinnedTo('end');
  const pinned = new Set([...start, ...end]);

  const middle = definitions.filter(d => !pinned.has(d.id)).map(d => d.id);
  const known = new Set(middle);
  const saved = order.filter(id => known.has(id));
  const seen = new Set(saved);

  return [...start, ...saved, ...middle.filter(id => !seen.has(id)), ...end];
};

// The descriptors ArchTable renders from: one per definition, in display order,
// carrying the resolved width and whether it is currently shown.
export function resolveColumns(definitions, layout) {
  const { order = [], hidden = [], widths = {} } = layout || {};
  const byId = new Map(definitions.map(d => [d.id, d]));
  const notShown = new Set(hidden);

  return orderedIds(definitions, order).map(id => {
    const definition = byId.get(id);
    return {
      id,
      label: columnProp(definition, 'label'),
      element: definition.element,
      pinned: definition.pinned || null,
      minWidth: definition.minWidth || MIN_COLUMN_WIDTH,
      // Pinned columns are the way in and out of a row, so they always show.
      hidden: !definition.pinned && notShown.has(id),
      width: widthFor(definition, widths),
    };
  });
}

// Visibility only. `order` is deliberately untouched: deriving it from a set
// operation is how the workstations column menu loses the user's arrangement.
export const toggleHidden = (layout, id) => ({
  ...layout,
  hidden: layout.hidden.includes(id)
    ? layout.hidden.filter(other => other !== id)
    : [...layout.hidden, id],
});

// The full list from the drag, not a pair of indices, so order is stored rather
// than inferred.
export const reorder = (layout, nextOrder) => ({
  ...layout,
  order: [...nextOrder],
});

export const setWidth = (layout, id, px) => ({
  ...layout,
  widths: { ...layout.widths, [id]: Math.round(px) },
});

export const totalWidth = columns =>
  columns.filter(c => !c.hidden).reduce((sum, c) => sum + c.width, 0);
